import { Badge } from '@mantine/core';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';
import { joinPartyNames } from '@dealstack/frontend/app/pages/Project/Cap/BatchAnalysis/components/partyNames';
import type { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import type { CurrencyType } from '@dealstack/shared/lib/generated';
import type {
	BatchDocumentTransactions,
	EnrichedExternalTransaction,
	ExternalTransaction,
} from '@dealstack/frontend/app/services/DocumentExtraction.service';
import type { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getConfidenceColor } from '../utils/confidenceStyling.helper';

export interface TransactionRow {
	[key: string]: unknown;
	transactionId: string;
	transactionType: string;
	sellers: string;
	buyers: string;
	securityType: string;
	numberOfUnits: number;
	costValue: number | undefined;
	costCurrency: string | undefined;
	transactionDate: string | null;
	confidence: number;
	enrichedTransaction: EnrichedExternalTransaction;
}

export const getColumns = (
	t: TranslateFunction<'cap-manage'>
): ExtendedColumnDef<TransactionRow>[] => [
	{
		accessorKey: 'transactionType',
		header: t('transactions.add_from_document.column_type'),
		cell: ({ row }) => (
			<Badge
				size="xs"
				variant="light"
				color="blue"
				styles={{ root: { textTransform: 'none' } }}
			>
				{row.original.transactionType || 'Unknown'}
			</Badge>
		),
	},
	{ accessorKey: 'sellers', header: t('transactions.add_from_document.column_sellers') },
	{ accessorKey: 'buyers', header: t('transactions.add_from_document.column_buyers') },
	{ accessorKey: 'securityType', header: t('transactions.add_from_document.column_security') },
	{
		accessorKey: 'numberOfUnits',
		header: t('transactions.add_from_document.column_units'),
		cell: ({ row }) => row.original.numberOfUnits?.toLocaleString() ?? '–',
	},
	{
		accessorKey: 'costValue',
		header: t('transactions.add_from_document.column_cost'),
		cell: ({ row }) => (
			<AmountFormatted
				value={row.original.costValue}
				currency={row.original.costCurrency as CurrencyType | undefined}
				showZero
			/>
		),
	},
	{
		accessorKey: 'transactionDate',
		header: t('transactions.add_from_document.column_date'),
		cell: ({ row }) => row.original.transactionDate || '–',
	},
	{
		accessorKey: 'confidence',
		header: t('transactions.add_from_document.column_confidence'),
		cell: ({ row }) => (
			<Badge
				size="xs"
				variant="light"
				color={getConfidenceColor(row.original.confidence)}
				styles={{ root: { textTransform: 'none' } }}
			>
				{Math.round(row.original.confidence * 100)}%
			</Badge>
		),
	},
];

const enrichTransaction = (
	tx: ExternalTransaction,
	doc: BatchDocumentTransactions
): EnrichedExternalTransaction => ({
	...tx,
	documentId: doc.documentId,
	fileName: doc.fileName,
	fileUrl: doc.fileUrl,
});

export const toRow = (tx: ExternalTransaction, doc: BatchDocumentTransactions): TransactionRow => ({
	transactionId: tx.transactionId,
	transactionType: tx.transactionType || 'Unknown',
	sellers: joinPartyNames(tx.sellers),
	buyers: joinPartyNames(tx.buyers),
	securityType: tx.securityType || '–',
	numberOfUnits: tx.numberOfUnits,
	costValue: tx.priceCost?.value,
	costCurrency: tx.priceCost?.currency,
	transactionDate: tx.transactionDate,
	confidence: tx.confidence,
	enrichedTransaction: enrichTransaction(tx, doc),
});
